import { io, type Socket } from 'socket.io-client';
import { api, getToken } from './api';

export interface TokenResult {
  url: string;
  token: string;
  identity: string;
  role: string;
  canPublish: boolean;
  expiresInSeconds: number;
}

export interface RoomView {
  id: string;
  kind: 'DESK' | 'ADHOC';
  name: string;
  market: { id: string; code: string } | null;
  shareMode: 'OPEN' | 'REQUEST';
  shareModeLabel: string;
  createdBy: { id: string; name: string } | null;
  context: { type: string; id: string; href: string | null } | null;
  closedAt: string | null;
  canModerate: boolean;
  members: { id: string; name: string }[] | null;
  participants: { identity: string; name: string; sharing: boolean; joinedAt: string }[];
  myShareRequest: string | null;
  pendingRequests: { userId: string; name: string; requestedAt: string }[];
  grantedShares: { userId: string; name: string }[];
}

export interface WebinarSummary {
  id: string;
  title: string;
  description: string | null;
  startsAt: string;
  endsAt: string;
  durationMinutes: number;
  status: 'SCHEDULED' | 'LIVE' | 'ENDED' | 'CANCELLED';
  layout: 'CHART_WEBCAM' | 'SPEAKER' | 'SIDE_BY_SIDE';
  audienceMode: 'FLOOR' | 'MARKETS' | 'SELECTED' | 'GUESTS';
  audienceLabel: string;
  host: { id: string; name: string };
  coHosts: { id: string; name: string }[];
  attachments: { id: string; title: string; type: string }[];
  liveAt: string | null;
  endedAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  /** INT-STR-06: public videos once it has been streamed */
  links: { youtube: string | null; facebook: string | null };
  relation: 'HOST' | 'COHOST' | 'STAFF' | 'MEMBER';
  registered: boolean;
  canManage: boolean;
}
export interface WebinarView extends WebinarSummary {
  markets: { id: string; code: string; name: string }[];
  selected: { id: string; name: string }[];
  canEdit: boolean;
  canRun: boolean;
  canJoin: boolean;
  backstageFrom: string;
  guestLink: string | null;
  capacity: { max: number; inRoom: number };
  counts: { registered: number; joined?: number; guests?: number; guestsJoined?: number; inRoom?: number };
  livekit: { configured: boolean };
}

export interface Interaction {
  status: WebinarSummary['status'];
  layout: WebinarSummary['layout'];
  chat: { id: string; authorName: string; host: boolean; mine: boolean; body: string; hidden: boolean; createdAt: string }[];
  questions: { id: string; authorName: string; mine: boolean; body: string; status: 'OPEN' | 'ANSWERED' | 'HIDDEN'; votes: number; iVoted: boolean; createdAt: string }[];
  hands: { identity: string; name: string; status: 'RAISED' | 'ON_STAGE'; raisedAt: string; guest: boolean }[];
  myHand: 'RAISED' | 'ON_STAGE' | null;
  manager: boolean;
}

/** The webinar calls a member and a guest make differ only in path and credential; the live view uses this seam. */
export interface LiveApi {
  interaction: () => Promise<Interaction>;
  token: () => Promise<TokenResult>;
  chat: (body: string) => Promise<unknown>;
  ask: (body: string) => Promise<unknown>;
  vote: (questionId: string) => Promise<unknown>;
  raise: () => Promise<unknown>;
  lower: () => Promise<unknown>;
  /** hosts only */
  moderateChat?: (id: string, action: 'HIDE' | 'UNHIDE') => Promise<unknown>;
  moderateQuestion?: (id: string, action: 'ANSWER' | 'REOPEN' | 'HIDE' | 'UNHIDE') => Promise<unknown>;
  stage?: (identity: string, onStage: boolean) => Promise<unknown>;
}

export const memberLiveApi = (id: string): LiveApi => ({
  interaction: () => api(`/webinars/${id}/interaction`),
  token: () => api(`/webinars/${id}/token`, { body: {} }),
  chat: (body) => api(`/webinars/${id}/chat`, { body: { body } }),
  ask: (body) => api(`/webinars/${id}/questions`, { body: { body } }),
  vote: (q) => api(`/webinars/${id}/questions/${q}/vote`, { body: {} }),
  raise: () => api(`/webinars/${id}/hand`, { body: {} }),
  lower: () => api(`/webinars/${id}/hand`, { method: 'DELETE' }),
  moderateChat: (m, action) => api(`/webinars/${id}/chat/${m}`, { body: { action } }),
  moderateQuestion: (q, action) => api(`/webinars/${id}/questions/${q}`, { body: { action } }),
  stage: (identity, onStage) => api(`/webinars/${id}/hands/${encodeURIComponent(identity)}`, { body: { onStage } }),
});

/** Guest calls carry the guest's own session header and no member token (INT-WEB-06). */
export async function guestFetch<T>(path: string, session: string | null, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(`/api/v1/guest${path}`, {
    method: opts.method ?? (opts.body !== undefined ? 'POST' : 'GET'),
    headers: { ...(opts.body !== undefined ? { 'content-type': 'application/json' } : {}), ...(session ? { 'x-webinar-guest': session } : {}) },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) {
    const b = (await res.json().catch(() => ({}))) as { message?: string; code?: string };
    throw Object.assign(new Error(b.message ?? res.statusText), { status: res.status, code: b.code });
  }
  return (await res.json()) as T;
}

export const guestLiveApi = (session: string): LiveApi => ({
  interaction: () => guestFetch('/webinar/interaction', session),
  token: () => guestFetch('/webinar/token', session, { body: {} }),
  chat: (body) => guestFetch('/webinar/chat', session, { body: { body } }),
  ask: (body) => guestFetch('/webinar/questions', session, { body: { body } }),
  vote: (q) => guestFetch(`/webinar/questions/${q}/vote`, session, { body: {} }),
  raise: () => guestFetch('/webinar/hand', session, { body: {} }),
  lower: () => guestFetch('/webinar/hand', session, { method: 'DELETE' }),
});

/**
 * A realtime watcher for one room or webinar: events are "refetch" hints, REST stays the truth. Members ask the server
 * to subscribe (it checks access); a guest's socket authenticates with the guest session and only ever sees its webinar.
 */
export function watchLive(opts: { kind: 'room' | 'webinar'; id: string; guestSession?: string }, onEvent: () => void): () => void {
  const socket: Socket = io({
    path: '/api/v1/socket',
    auth: (cb) => cb(opts.guestSession ? { guestToken: opts.guestSession } : { token: getToken() }),
    transports: ['websocket', 'polling'],
  });
  socket.on('ready', () => {
    if (!opts.guestSession) socket.emit('live.watch', { kind: opts.kind, id: opts.id });
  });
  socket.onAny((ev: string) => {
    if (/^(room|rooms|webinar|webinars|attendance|chat|questions|hands|stream|recording)[.]/.test(ev)) onEvent();
  });
  return () => {
    socket.disconnect();
  };
}

export const WEBINAR_STATUS_TONE = { SCHEDULED: 'accent', LIVE: 'up', ENDED: 'neutral', CANCELLED: 'down' } as const;
export const WEBINAR_STATUS_LABEL = { SCHEDULED: 'Scheduled', LIVE: 'Live now', ENDED: 'Ended', CANCELLED: 'Cancelled' } as const;

/** A `datetime-local` value ("2026-11-10T09:00") read as wall-clock time in an IANA zone → the UTC instant. */
export function zonedToUtc(local: string, tz: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local);
  if (!m) return new Date(NaN);
  const asUtc = Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!);
  const offsetAt = (t: number) => {
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).formatToParts(new Date(t)).map((x) => [x.type, x.value]));
    return Date.UTC(+p.year!, +p.month! - 1, +p.day!, +p.hour!, +p.minute!, +p.second!) - t;
  };
  let t = asUtc - offsetAt(asUtc);
  t = asUtc - offsetAt(t);
  return new Date(t);
}

/** The reverse: a UTC instant as a `datetime-local` value in the zone. */
export function utcToZoned(d: string | Date, tz: string): string {
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).formatToParts(new Date(d)).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/** Downloads the member's .ics for a webinar (the endpoint needs the bearer token, so a plain link won't do). */
export async function downloadIcs(id: string, href = `/api/v1/webinars/${id}/ics`, headers: Record<string, string> = { Authorization: `Bearer ${getToken() ?? ''}` }) {
  const res = await fetch(href, { headers, credentials: 'include' });
  if (!res.ok) throw new Error('Could not download the calendar file.');
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = `webinar-${id}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
